import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cache = {};
let modeCache = null;
let modeCachePromise = null;

/**
 * useMonetizationMode — Reads the global monetization mode.
 * In "disabled" mode, the UI must hide all paywalls and locked badges.
 * In "soft" mode, plans/limits are displayed but features remain unlocked.
 * In "enabled" mode, full enforcement applies.
 */
export function useMonetizationMode() {
  const [state, setState] = useState(modeCache || {
    loading: !modeCache, mode: 'disabled', enforced: false,
    show_paywalls: false, show_plans: false, marketing_message: null,
  });

  useEffect(() => {
    if (modeCache) { setState({ ...modeCache, loading: false }); return; }
    if (!modeCachePromise) {
      modeCachePromise = axios.get(`${API_URL}/api/monetization/mode`)
        .then(({ data }) => { modeCache = data; return data; })
        .catch(() => ({ mode: 'disabled', enforced: false, show_paywalls: false, show_plans: false }));
    }
    modeCachePromise.then(d => setState({ ...d, loading: false }));
  }, []);

  return state;
}

export function clearMonetizationModeCache() {
  modeCache = null;
  modeCachePromise = null;
}

export function useEntitlement(featureCode) {
  const [state, setState] = useState({ loading: true, allowed: false, type: 'none', value: null, limit: 0 });

  useEffect(() => {
    if (!featureCode) return;
    const key = `ent_${featureCode}`;
    if (cache[key]) {
      setState({ ...cache[key], loading: false });
      return;
    }
    axios.get(`${API_URL}/api/monetization/my/check/${featureCode}`, { withCredentials: true })
      .then(({ data }) => {
        cache[key] = data;
        setState({ ...data, loading: false });
      })
      .catch(() => setState(s => ({ ...s, loading: false })));
  }, [featureCode]);

  return state;
}

export function useSubscription() {
  const [sub, setSub] = useState({ loading: true, plan_code: 'player_free', plan_name: 'Free', ai_usage: null, entitlements: {} });

  useEffect(() => {
    axios.get(`${API_URL}/api/monetization/my/subscription`, { withCredentials: true })
      .then(({ data }) => setSub({ ...data, loading: false }))
      .catch(() => setSub(s => ({ ...s, loading: false })));
  }, []);

  const refresh = useCallback(() => {
    axios.get(`${API_URL}/api/monetization/my/subscription`, { withCredentials: true })
      .then(({ data }) => setSub({ ...data, loading: false }));
  }, []);

  return { ...sub, refresh };
}

export function useQuota(featureCode) {
  const [quota, setQuota] = useState({ loading: true, allowed: true, used: 0, limit: 0, remaining: 0 });

  const refresh = useCallback(() => {
    if (!featureCode) return;
    axios.get(`${API_URL}/api/monetization/my/subscription`, { withCredentials: true })
      .then(({ data }) => {
        if (data.ai_usage && featureCode === 'ai.rule_questions') {
          setQuota({ ...data.ai_usage, loading: false });
        }
      })
      .catch(() => {});
  }, [featureCode]);

  useEffect(() => { refresh(); }, [refresh]);

  const trackUsage = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/monetization/my/use/${featureCode}`, {}, { withCredentials: true });
      setQuota(q => ({ ...q, used: data.used, remaining: data.remaining, allowed: data.remaining > 0 }));
      return { allowed: true };
    } catch (e) {
      if (e.response?.status === 403) {
        setQuota(q => ({ ...q, allowed: false, remaining: 0 }));
        return { allowed: false, message: e.response?.data?.detail };
      }
      return { allowed: true };
    }
  }, [featureCode]);

  return { ...quota, refresh, trackUsage };
}

// Clear cache (call after plan changes)
export function clearEntitlementCache() {
  Object.keys(cache).forEach(k => delete cache[k]);
}

/**
 * useSmartNudges — Fetches and manages smart upgrade nudges based on user behavior.
 * Returns: { nudges, loading, dismiss(nudgeId), refresh() }
 */
export function useSmartNudges() {
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNudges = useCallback(() => {
    axios.get(`${API_URL}/api/monetization/my/nudges`, { withCredentials: true })
      .then(({ data }) => {
        setNudges(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchNudges(); }, [fetchNudges]);

  const dismiss = useCallback((nudgeId) => {
    setNudges(prev => prev.filter(n => n.nudge_id !== nudgeId));
  }, []);

  return { nudges, loading, dismiss, refresh: fetchNudges };
}
